import React, { Suspense } from 'react';
import { useRoutes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PrivateRoute from './PrivateRoute';
import Root from './Root';

// All layouts/containers
import DefaultLayout from '../layouts/Default';
import DetachedLayout from '../layouts/Detached';

import AddStation from '../pages/dashboard/Dashboard/station/AddStation';
import AddZone from '../pages/dashboard/Dashboard/zone/AddZone';
import AddDivision from '../pages/dashboard/Dashboard/divisions/AddDivision';
import Notification from '../pages/alert/Notification';
import Profile from '../pages/profile';

import MasterImport from '../pages/MasterImport';
import AddVendors from '../pages/dashboard/Dashboard/vendors/AddVendors';
import AddUsers from '../pages/dashboard/Dashboard/users/AddUsers';
import AlertSummary from '../pages/reports/AlertSummary';
import AlertDetails from '../pages/reports/AlertDetails';
import TelemetryHistory from '../pages/reports/TelemetryHistory';
import Asset from '../pages/reports/Asset';

import PredictiveURL from '../pages/predictive/PredictiveURL';
import AlertLive from '../pages/reports/AlertLive';
import MaintenanceMode from '../pages/reports/MaintenanceMode';
import TelemetryLive from '../pages/reports/TelemetryLive';
import Performance from '../pages/reports/Performance';
import RDPMSHealthLive from '../pages/reports/RDPMSHealthLive';
import RDPMSHealthSummary from '../pages/reports/RDPMSHealthSummary';
import EquipmentRoomLive from '../pages/reports/EquipmentRoomLive';
import EquipmentRoomHistory from '../pages/reports/EquipmentRoomHistory';
import AssetUtilization from '../pages/reports/AssetUtilization';
import VersionHistory from '../pages/VersionHistory/VersionHistory';
import EquipmentMaster from '../pages/dashboard/Dashboard/equipment/EquipmentMaster';
import HelpDocumentation from '../pages/Help-Document/HelpDocumentation';

// auth
const Login = React.lazy(() => import('../pages/account/Login'));
const Logout = React.lazy(() => import('../pages/account/Logout'));
const Register = React.lazy(() => import('../pages/account/Register'));
const Confirm = React.lazy(() => import('../pages/account/Confirm'));
const ForgetPassword = React.lazy(() => import('../pages/account/ForgetPassword'));
const LockScreen = React.lazy(() => import('../pages/account/LockScreen'));

// dashboard
const Dashboard = React.lazy(() => import('../pages/dashboard/Dashboard'));

// 404 page — NEW IMPORT ADDED
const NotFound = React.lazy(() => import('../pages/NotFound'));

const loading = () => <div className=""></div>;

const LoadComponent = ({ component: Component }) => (
    <Suspense fallback={loading()}>
        <Component />
    </Suspense>
);

const AllRoutes = () => {
    const { auth } = useSelector((state) => ({
        auth: state.Auth,
    }));

    let authCred = '';
    authCred = auth && auth.user && auth.user.role;
    // console.log(authCred, 'authCred......');

    const getLayout = () => {
        let layoutCls = DetachedLayout;
        return layoutCls;
    };
    let Layout = getLayout();

    return useRoutes([
        { path: '/', element: <Root /> },
        {
            // public routes
            path: '/',
            element: <DefaultLayout />,
            children: [
                {
                    path: 'account',
                    children: [
                        { path: 'login', element: <LoadComponent component={Login} /> },
                        { path: 'register', element: <LoadComponent component={Register} /> },
                        { path: 'confirm', element: <LoadComponent component={Confirm} /> },
                        { path: 'forget-password', element: <LoadComponent component={ForgetPassword} /> },
                        { path: 'lock-screen', element: <LoadComponent component={LockScreen} /> },
                        { path: 'logout', element: <LoadComponent component={Logout} /> },
                    ],
                },
            ],
        },
        {
            // auth protected routes
            path: '/',
            element: <PrivateRoute roles={authCred} component={Layout} />,
            children: [
                {
                    path: 'rdpms',
                    children: [
                        {
                            path: 'dashboard',
                            element:
                                authCred === 'admin' ? (
                                    <LoadComponent component={Dashboard} />
                                ) : authCred === 'user' ? (
                                    <LoadComponent component={Dashboard} />
                                ) : (
                                    ''
                                ),
                        },
                        {
                            path: 'notification',
                            element: <LoadComponent component={Notification} />,
                        },
                        {
                            path: 'versionhistory',
                            element: <LoadComponent component={VersionHistory} />,
                        },
                        {
                            path: 'masterimport',
                            element: <LoadComponent component={MasterImport} />,
                        },
                        {
                            path: 'documentation',
                            element: <LoadComponent component={HelpDocumentation} />,
                        },
                        {
                            path: 'predictive',
                            element: <LoadComponent component={PredictiveURL} />,
                        },
                        {
                            path: 'alertsummary',
                            element: <LoadComponent component={AlertSummary} />,
                        },
                        {
                            path: 'maintenancemode',
                            element: <LoadComponent component={MaintenanceMode} />,
                        },
                        {
                            path: 'alertlive',
                            element: <LoadComponent component={AlertLive} />,
                        },
                        {
                            path: 'alertdetails',
                            element: <LoadComponent component={AlertDetails} />,
                        },
                        {
                            path: 'telemetryhistory',
                            element: <LoadComponent component={TelemetryHistory} />,
                        },
                        {
                            path: 'telemetrylive',
                            element: <LoadComponent component={TelemetryLive} />,
                        },
                        {
                            path: 'rdpmshealthlive',
                            element: <LoadComponent component={RDPMSHealthLive} />,
                        },
                        {
                            path: 'rdpmshealthsummary',
                            element: <LoadComponent component={RDPMSHealthSummary} />,
                        },
                        {
                            path: 'equipmentroomlive',
                            element: <LoadComponent component={EquipmentRoomLive} />,
                        },
                        {
                            path: 'equipmentroomhistory',
                            element: <LoadComponent component={EquipmentRoomHistory} />,
                        },
                        {
                            path: 'assets',
                            element: <LoadComponent component={Asset} />,
                        },
                        {
                            path: 'assetutilization',
                            element: <LoadComponent component={AssetUtilization} />,
                        },
                        {
                            path: 'performance',
                            element: <LoadComponent component={Performance} />,
                        },
                        {
                            path: 'station',
                            element: <LoadComponent component={AddStation} />,
                        },
                        {
                            path: 'zone',
                            element: <LoadComponent component={AddZone} />,
                        },
                        {
                            path: 'division',
                            element: <LoadComponent component={AddDivision} />,
                        },
                        {
                            path: 'vendor',
                            element: <LoadComponent component={AddVendors} />,
                        },
                        {
                            path: 'user',
                            element: <LoadComponent component={AddUsers} />,
                        },
                        {
                            path: 'equipment',
                            element: <LoadComponent component={EquipmentMaster} />,
                        },
                        {
                            path: 'profile',
                            element: <LoadComponent component={Profile} />,
                        },
                    ],
                },
            ],
        },
        // 404 catch-all — NEW BLOCK ADDED (must stay LAST in this array)
        {
            path: '*',
            element: <LoadComponent component={NotFound} />,
        },
    ]);
};

export { AllRoutes };
